import React from "react";

// Styling Imports
import { createStyles, makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import {TopAppBar, AllProps as AppBarProps} from "../organisms/TopAppBar";
import {IMenuItem, LeftMenu, AllProps as LeftMenuProps} from "../organisms/LeftMenu";

// Data Imports
import { connect } from "react-redux";
import * as UserInfoData from "../../store/user/userInfo";
import * as Navigate from "../../store/navigate"
import { IApplicationState } from "../../store";
import { Dispatch } from "redux";
import { IReportDataObject } from "../../entities/reports/IReportData";
import { FetchState } from "../../entities/FetchState";
import ErrorMessage from "../organisms/ErrorMessage";


// CSS
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
    },
    horizontal: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
    },
    vertical: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    },
  }),
); 


// Standard Properties
export interface Page {
  inMenu: boolean;
  url: string;
  text: string;
  icon: React.ReactNode;
  useWindowLocation?: boolean;
  onClick?: () => void; // If this is set, clicking the menu item will trigger this instead of going to the url directly
  requiredPermission?: (userInfo: UserInfoData.IData) => boolean; // Optional permission check function
}

export interface IProps {
  children?: React.ReactNode;
  pages: Page[];
}

// Props from State
interface IPropsFromState {
  userInfo: IReportDataObject<UserInfoData.IData>;
}
const mapStateToProps = (state: IApplicationState): IPropsFromState => ({
  userInfo: state.user.userInfo.UserInfo,
});

// Props from Dispatch
interface IPropsFromDispatch {
  loadUserInfo: () => void;
  NavigateToUrl: (url: string) => void;
}
const mapDispatchToProps = (dispatch: Dispatch): IPropsFromDispatch => ({
  loadUserInfo: () => dispatch(UserInfoData.ActionCreators.FetchData()),
  NavigateToUrl: (url: string) => dispatch(Navigate.ActionCreators.ToUrl(url)),
});


// Combine all props
export type AllProps = IProps & IPropsFromState & IPropsFromDispatch;

// Actual Component
export const MainPageLayout = (props: AllProps) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  
  if (props.userInfo.state === FetchState.NotFetched) {
    props.loadUserInfo();
  }

  if (props.userInfo.state === FetchState.Error) {
    return <ErrorMessage 
      title="Algemene Fout"
      message={`Er is een fout opgetreden bij het ophalen van gebruikersgegevens. Start de applicatie opnieuw.`}
      options={[{
        optionLabel: "Herstart de applicatie",
        optionExecute: () => window.location.reload(),
      }]}
      errorDetails={props.userInfo.errorMessage}
    />;
  }

  const pages: IMenuItem[] = props.pages
    .filter(page => page.inMenu)
    .filter(page => {
      // If no permission is required, show the page
      if (!page.requiredPermission) return true;
      // If user data is not loaded yet, hide the page
      if (props.userInfo.state !== FetchState.Success) return false;
      // Check if user has the required permission using userInfo
      return page.requiredPermission(props.userInfo.data);
    })
    .map((page) => ({
      text: page.text,
      icon: page.icon,
      onClick: () => {
        console.debug(`Navigating to ${page.url}`);
        if (page.useWindowLocation) {
          console.warn(`Redirecting to ${page.url}`); 
          window.location.href = page.url 
        } else { 
          page.onClick ? page.onClick() : props.NavigateToUrl(page.url); 
        }
      },
    }));


  const displayName = props.userInfo.state === FetchState.Success 
    ? `${props.userInfo.data.firstName} ${props.userInfo.data.lastName}` 
    : "Loading...";

  return (
    <div className={classes.root}>
      <div className={classes.horizontal}>
        <LeftMenu items={pages} />
        <div className={classes.vertical}>
          <TopAppBar medewerkerName={displayName} />
          <div className={classes.content}>
        {props.children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default connect<IPropsFromState, IPropsFromDispatch, {}, IApplicationState>(mapStateToProps, mapDispatchToProps)(MainPageLayout);